/**
 * Contacts list page - thin wrapper around generic CRMObjectListPage.
 */

import { MessageChatCircle, Stars02 } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useUser } from '@/allauth_auth/hooks';
import { useOrg } from '@/contexts/OrgContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  BulkEnrichAutoModal,
  BulkEnrichModal,
} from '@/lib/crm/components/enrichment';
import type {
  BulkAction,
  BulkActionRequirement,
  ListPageConfig,
  RowAction,
  RowActionContext,
} from '@/lib/crm/core/types';
import { useContactEmailDedupPrefetch } from '@/lib/crm/hooks/useContactEmailDedupPrefetch';
import { useCreateDeal } from '@/lib/crm/hooks/useCreateDeal';
import { contactListConfig, CRMObjectListPage } from '@/lib/crm/list';
import { useChatAboutRecords } from '@/lib/crm/list/hooks/useChatAboutRecords';
import { contactService } from '@/lib/crm/services';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';
import { setActiveBulkActionJob } from '@/lib/localStorage/bulkActionActiveJob';
import { useViewHealthMarker } from '@/lib/view-health';
import type {
  ContactCreateData,
  CrmObject,
  CrmObjectFilters,
  FilterGroup,
} from '@/types/crm';

const DealIcon = getEntityTypeIconComponent('ahoy_deal');

interface BulkSelectionState {
  isSelectAllMode: boolean;
  totalCount: number;
  filterGroups?: FilterGroup[];
  excludedEntityIds?: string[];
  search?: string;
  searchProperties?: string[];
}

const EMPTY_SELECTION: BulkSelectionState = {
  isSelectAllMode: false,
  totalCount: 0,
};

export default function ContactsList() {
  const user = useUser();
  const { currentOrg } = useOrg();

  // Debounced email-dedup prefetch: warms contactService's dedup cache as the
  // user types the email in the create-contact form, so create() can skip its
  // pre-submit lookup round-trip (see contactService.prefetchExistingByEmail).
  const handleCreatePropertyChange = useContactEmailDedupPrefetch(
    currentOrg?.id
  );
  useViewHealthMarker('contacts-list-ready', { ready: true });
  const [enrichModalOpen, setEnrichModalOpen] = useState(false);
  const [enrichContacts, setEnrichContacts] = useState<CrmObject[]>([]);
  const [enrichAutoModalOpen, setEnrichAutoModalOpen] = useState(false);
  const [enrichAutoObjects, setEnrichAutoObjects] = useState<CrmObject[]>([]);
  const [enrichAutoSelection, setEnrichAutoSelection] =
    useState<BulkSelectionState>(EMPTY_SELECTION);
  const chatAboutRecords = useChatAboutRecords();
  const isChatAboutEnabled = useFeatureFlag('chat_about_records');

  const {
    createDealForContact,
    createDealForContacts,
    createDealForContactsByFilters,
  } = useCreateDeal(currentOrg?.id ?? 0);

  const handleBulkCreateDeal = useCallback(
    (objects: CrmObject[]) => {
      void createDealForContacts(objects);
    },
    [createDealForContacts]
  );

  const handleBulkCreateDealWithFilters = useCallback(
    (
      filters: CrmObjectFilters,
      excludedEntityIds: string[],
      totalCount: number
    ) => {
      void createDealForContactsByFilters(
        filters,
        excludedEntityIds,
        totalCount
      );
    },
    [createDealForContactsByFilters]
  );

  // Handler for single-row enrich action
  const handleRowEnrich = useCallback(
    (object: CrmObject, context?: RowActionContext) => {
      const eligibility = context?.enrichmentEligibility;

      // Show toast if not eligible (always-enabled pattern with click feedback)
      if (eligibility && !eligibility.isEligible) {
        toast.info(
          eligibility.disabledReason || 'Phone enrichment not available'
        );
        return;
      }

      // Open modal if eligible
      setEnrichContacts([object]);
      setEnrichModalOpen(true);
    },
    []
  );

  const handleBulkEnrichAuto = useCallback((objects: CrmObject[]) => {
    setEnrichAutoObjects(objects);
    setEnrichAutoSelection({
      isSelectAllMode: false,
      totalCount: objects.length,
    });
    setEnrichAutoModalOpen(true);
  }, []);

  const handleBulkEnrichAutoWithFilters = useCallback(
    (
      filters: CrmObjectFilters,
      excludedEntityIds: string[],
      totalCount: number
    ) => {
      setEnrichAutoObjects([]);
      setEnrichAutoSelection({
        isSelectAllMode: true,
        totalCount,
        filterGroups: filters.filter_groups,
        excludedEntityIds,
        search: filters.search,
        searchProperties: filters.search_properties,
      });
      setEnrichAutoModalOpen(true);
    },
    []
  );

  const handleCloseEnrich = useCallback(() => {
    setEnrichModalOpen(false);
    setEnrichContacts([]);
  }, []);

  const handleCloseEnrichAuto = useCallback(() => {
    setEnrichAutoModalOpen(false);
    setEnrichAutoObjects([]);
    setEnrichAutoSelection(EMPTY_SELECTION);
  }, []);

  // Async job dispatch — persist the active job so BulkActionBanner picks it up
  // on the next render (or after navigation).
  const handleAsyncDispatched = useCallback(
    (jobId: number, totalCount: number) => {
      if (user?.id && currentOrg?.id) {
        setActiveBulkActionJob(
          user.id,
          currentOrg.id,
          jobId,
          totalCount,
          'ahoy_contact'
        );
      }
    },
    [user?.id, currentOrg?.id]
  );

  // Sync completion — CRM WebSocket broadcasts the resulting events
  // (ahoy_association_added for stage change, ahoy_object_created for
  // enrichment requests), so the list updates automatically without an
  // explicit refetch trigger here.
  const handleSyncComplete = useCallback(() => {}, []);

  // Build config with enrichment actions
  const configWithEnrichment = useMemo((): ListPageConfig => {
    // Enrichment creates an ahoy_enrichment_request per contact (not a
    // property write on the contact) — the server checks exactly that grant.
    const enrichRequirement: BulkActionRequirement = {
      permission: ['ahoy_enrichment_request', 'ahoy_object_created'],
    };
    const createDealRequirement: BulkActionRequirement = {
      permission: ['ahoy_deal', 'ahoy_object_created'],
    };

    const bulkEnrichAutoAction: BulkAction = {
      id: 'enrich-auto',
      label: 'Enrich',
      icon: Stars02,
      priority: 'primary',
      requires: enrichRequirement,
      onClick: handleBulkEnrichAuto,
      onClickWithFilters: handleBulkEnrichAutoWithFilters,
    };

    const bulkChatAction: BulkAction = {
      id: 'chat-about-records',
      label: 'Chat about these records',
      icon: MessageChatCircle,
      priority: 'primary',
      requires: { feature: 'chat' },
      onClick: chatAboutRecords.onClick,
      onClickWithFilters: chatAboutRecords.onClickWithFilters,
    };

    const bulkCreateDealAction: BulkAction = {
      id: 'create-deal',
      label: 'Create deal',
      icon: DealIcon,
      priority: 'secondary',
      requires: createDealRequirement,
      onClick: handleBulkCreateDeal,
      onClickWithFilters: handleBulkCreateDealWithFilters,
    };

    const rowEnrichAction: RowAction = {
      id: 'enrich-phone',
      label: 'Enrich phone',
      icon: Stars02,
      requires: enrichRequirement,
      onClick: handleRowEnrich,
      // Always enabled - eligibility checked in onClick with toast feedback
    };

    const rowCreateDealAction: RowAction = {
      id: 'create-deal',
      label: 'Create deal',
      icon: DealIcon,
      requires: createDealRequirement,
      onClick: object => createDealForContact(object),
    };

    return {
      ...contactListConfig,
      // Contacts are pipelineable via their lifecycle-stage association. This is
      // the single switch CRMObjectListPage reads for both the Board CTA
      // (crm-object-list-page.tsx) and the ?view=board route.
      stageAssociationTypeName: 'ahoy_contact_to_lifecycle_stage',
      bulkActions: [
        ...(contactListConfig.bulkActions ?? []),
        bulkEnrichAutoAction,
        ...(isChatAboutEnabled ? [bulkChatAction] : []),
        bulkCreateDealAction,
      ],
      rowActions: [
        ...(contactListConfig.rowActions ?? []),
        rowEnrichAction,
        rowCreateDealAction,
      ],
    };
  }, [
    handleBulkEnrichAuto,
    handleBulkEnrichAutoWithFilters,
    chatAboutRecords,
    isChatAboutEnabled,
    handleRowEnrich,
    handleBulkCreateDeal,
    handleBulkCreateDealWithFilters,
    createDealForContact,
  ]);

  return (
    <>
      <CRMObjectListPage
        config={configWithEnrichment}
        onCreatePropertyChange={handleCreatePropertyChange}
        onCreateObject={async (orgId, data) => {
          if (!user?.id) {
            throw new Error('User not authenticated');
          }
          await contactService.create(
            orgId,
            // CrmCreateForm passes ahoy_* property names (ahoy_first_name, ahoy_email, etc.).
            // ContactCreateData is a legacy type — contactService.create internally re-casts to
            // Record<string, unknown> and accesses ahoy_* keys directly (see services.ts ~line 151).
            data as unknown as ContactCreateData,
            user.id
          );
        }}
      />

      {/* Bulk Enrich Modal - Only render when org is loaded */}
      {currentOrg?.id && (
        <BulkEnrichModal
          isOpen={enrichModalOpen}
          orgId={currentOrg.id}
          contacts={enrichContacts}
          onClose={handleCloseEnrich}
        />
      )}

      {currentOrg?.id && (
        <BulkEnrichAutoModal
          isOpen={enrichAutoModalOpen}
          orgId={currentOrg.id}
          contacts={enrichAutoObjects}
          isSelectAllMode={enrichAutoSelection.isSelectAllMode}
          totalCount={enrichAutoSelection.totalCount}
          filterGroups={enrichAutoSelection.filterGroups}
          excludedEntityIds={enrichAutoSelection.excludedEntityIds}
          search={enrichAutoSelection.search}
          searchProperties={enrichAutoSelection.searchProperties}
          onClose={handleCloseEnrichAuto}
          onAsyncDispatched={handleAsyncDispatched}
          onSyncComplete={handleSyncComplete}
        />
      )}
    </>
  );
}
